import React, { ReactNode, useCallback, useMemo } from 'react';
import { LanguageCode } from '../../../data/languages';

export type TranslationText = {
    [key in LanguageCode | string]?: string;
};

export interface UseTranslationOptions {
    defaultLanguage?: string;
    currentLanguage?: string;
}

export type TranslationParams = {
    [key: string]: string | number | ((children: ReactNode) => ReactNode);
};

export interface TranslateFunction {
    // Overload without params - returns string
    (
        text: TranslationText | string | undefined,
        fallback?: string
    ): string;
    // Overload with languageCode but no params - returns string
    (
        text: TranslationText | string | undefined,
        fallback: string | undefined,
        params: undefined,
        languageCode: string
    ): string;
    // Overload with params - returns string or ReactNode
    (
        text: TranslationText | string | undefined,
        fallback?: string,
        params?: TranslationParams,
        languageCode?: string
    ): string | ReactNode;
}

export interface UseTranslationResult {
    /**
     * Translate function that handles multi-language text objects
     * @param text - The text to translate (can be a string or MultiLangText object)
     * @param fallback - Optional fallback text if translation is not found
     * @param params - Optional parameters for string interpolation (e.g., {count: 10} for 'User has {count} points') or tag wrapping with functions (e.g., {i: (val) => <b>{val}</b>} for '<i>text</i>')
     * @param languageCode - Optional language code to override the current locale
     * @returns The translated string or ReactNode (when using function params for tag wrapping)
     */
    t: TranslateFunction;
    /**
     * Current locale code (e.g., 'en', 'fr', 'es')
     */
    locale: string;
}

/**
 * Hook for translating multi-language text objects
 * 
 * @example
 * ```tsx
 * const { t, locale } = useTranslation({ defaultLanguage: 'en' });
 * 
 * const multiLangText = {
 *   en: 'Hello',
 *   fr: 'Bonjour',
 *   es: 'Hola'
 * };
 * 
 * // Translates to current locale
 * const translated = t(multiLangText);
 * 
 * // With fallback
 * const withFallback = t(multiLangText, 'Default text');
 * 
 * // With params interpolation
 * const withParams = t('User has {count} points', '', { count: 10 });
 * // Result: 'User has 10 points'
 * 
 * // Escaped brackets are treated as literal text
 * const escaped = t('Use \\{count\\} as placeholder', '', { count: 10 });
 * // Result: 'Use {count} as placeholder'
 * 
 * // With function params for tag wrapping
 * const withTags = t('This is <i>formatted</i>', '', { 
 *   i: (value) => <b>{value}</b> 
 * });
 * // Result: ReactNode with <b>formatted</b>
 * 
 * // Escaped tags are treated as literal text
 * const escapedTags = t('This is \\<i>not formatted\\</i>', '', { 
 *   i: (value) => <b>{value}</b> 
 * });
 * // Result: 'This is <i>not formatted</i>'
 * 
 * // Mix of placeholders and tags
 * const mixed = t('User {name} has <b>{count}</b> points', '', { 
 *   name: 'John',
 *   count: 10,
 *   b: (value) => <strong>{value}</strong>
 * });
 * // Result: ReactNode with 'User John has <strong>10</strong> points'
 * 
 * // Override locale
 * const inFrench = t(multiLangText, '', undefined, 'fr');
 * ```
 */
export const useTranslation = (options: UseTranslationOptions = {}): UseTranslationResult => {
    const { defaultLanguage = 'en', currentLanguage } = options;

    // Get the current language from query params, browser, or fallback to default
    const locale = useMemo(() => {
        // Normalizes language codes like 'fr-FR' or 'pt_BR' to just 'fr' / 'pt'
        const normalizeLanguageCode = (code?: string | null) => {
            if (!code) return undefined;
            return code.split(/[-_]/)[0].toLowerCase();
        };

        if (currentLanguage) return currentLanguage;
        // Check for language query parameter
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const langFromQuery = urlParams.get('locale');
            const normalizedFromQuery = normalizeLanguageCode(langFromQuery);
            if (normalizedFromQuery) return normalizedFromQuery;
        }

        // Try to get browser language
        if (typeof navigator !== 'undefined') {
            const browserLang = normalizeLanguageCode(navigator.language); // e.g., 'en-US' -> 'en'
            if (browserLang) return browserLang;
        }

        return defaultLanguage;
    }, [defaultLanguage, currentLanguage]);


    const t = useCallback(
        (
            text: TranslationText | string | undefined,
            fallback?: string,
            params?: TranslationParams,
            languageCode?: LanguageCode | string
        ): string | ReactNode => {
            // Helper function to interpolate params into the translated string
            const interpolate = (str: string, params?: TranslationParams): string | ReactNode => {
                if (!params) {
                    // Even without params, we need to unescape any escaped characters
                    return str.replace(/\\([{}<>])/g, '$1');
                }

                // Replace escaped characters with sentinels so they are not matched
                // by tag/placeholder regexes (avoids lookbehind which breaks older Safari)
                const ESC_LT = '\x00ELT\x00';
                const ESC_GT = '\x00EGT\x00';
                const ESC_LB = '\x00ELB\x00';
                const ESC_RB = '\x00ERB\x00';
                const restoreSentinels = (s: string) =>
                    s.replace(/\x00ELT\x00/g, '<')
                     .replace(/\x00EGT\x00/g, '>')
                     .replace(/\x00ELB\x00/g, '{')
                     .replace(/\x00ERB\x00/g, '}');

                str = str
                    .replace(/\\</g, ESC_LT)
                    .replace(/\\>/g, ESC_GT)
                    .replace(/\\{/g, ESC_LB)
                    .replace(/\\}/g, ESC_RB);

                // First, process tag-based interpolation for functions
                // Match tags like <i>content</i> (escaped tags are already replaced with sentinels)
                const tagRegex = /<(\w+)>(.*?)<\/\1>/g;
                const hasTags = tagRegex.test(str);

                if (hasTags) {
                    // Reset regex lastIndex for reuse
                    tagRegex.lastIndex = 0;

                    const parts: (string | ReactNode)[] = [];
                    let lastIndex = 0;
                    let match;
                    let hasReactNodes = false;

                    while ((match = tagRegex.exec(str)) !== null) {
                        const [fullMatch, tagName, content] = match;
                        const param = params[tagName];

                        // Add text before the match
                        if (match.index > lastIndex) {
                            parts.push(str.substring(lastIndex, match.index));
                        }

                        // If param is a function, call it with the content
                        if (typeof param === 'function') {
                            parts.push(param(content));
                            hasReactNodes = true;
                        } else {
                            // If not a function, keep the original tag
                            parts.push(fullMatch);
                        }

                        lastIndex = match.index + fullMatch.length;
                    }

                    // Add remaining text after last match
                    if (lastIndex < str.length) {
                        parts.push(str.substring(lastIndex));
                    }

                    // If we have React nodes, process placeholder interpolation for each string part
                    if (hasReactNodes) {
                        const processedParts = parts.map((part, index) => {
                            if (typeof part === 'string') {
                                // Process {key} placeholders in string parts
                                let processed = Object.entries(params).reduce((acc, [key, value]) => {
                                    if (typeof value !== 'function') {
                                        const regex = new RegExp(`\\{${key}\\}`, 'g');
                                        return acc.replace(regex, String(value));
                                    }
                                    return acc;
                                }, part);

                                // Restore sentinels to literal characters
                                processed = restoreSentinels(processed);
                                return processed;
                            }
                            return part;
                        });

                        // If only one part and it's a string, return as string
                        if (processedParts.length === 1 && typeof processedParts[0] === 'string') {
                            return processedParts[0];
                        }

                        // Return array of React nodes
                        return processedParts.map((part, index) =>
                            typeof part === 'string' ? part : React.createElement('span', { key: index }, part)
                        );
                    }

                    // If no React nodes, continue with normal string processing
                    str = parts.join('');
                }

                // Process {key} placeholders for string/number values
                let result = Object.entries(params).reduce((acc, [key, value]) => {
                    if (typeof value !== 'function') {
                        const regex = new RegExp(`\\{${key}\\}`, 'g');
                        return acc.replace(regex, String(value));
                    }
                    return acc;
                }, str);

                // Restore sentinels to literal characters (\{ -> {, \} -> }, \< -> <, \> -> >)
                result = restoreSentinels(result);

                return result;
            };

            if (!text) return interpolate(fallback || '', params);

            if (typeof text === 'string') return interpolate(text, params);

            const targetLocale = languageCode || currentLanguage || locale;
            let translatedText = '';

            if (text[targetLocale]) {
                translatedText = text[targetLocale]!;
            } else if (fallback) {
                translatedText = fallback;
            } else if (text[defaultLanguage]) {
                translatedText = text[defaultLanguage]!;
            } else {
                const firstAvailable = Object.values(text).find(
                    (value) => value !== undefined && value !== ''
                );
                translatedText = firstAvailable || '';
            }

            return interpolate(translatedText, params);
        },
        [locale, defaultLanguage]
    ) as TranslateFunction;

    return { t, locale };
};

